import React from 'react';
import styles from './PortfolioCard.module.css';
import { useIntersectionObserver } from '../../../hooks/useIntersectionObserver';
import { PortfolioItem } from '../../../types';
import clsx from 'clsx';
import { FiExternalLink } from 'react-icons/fi';

interface PortfolioCardProps {
  item: PortfolioItem;
  index: number;
  onClick: () => void;
}

export const PortfolioCard: React.FC<PortfolioCardProps> = ({ item, index, onClick }) => {
  const [ref, isVisible] = useIntersectionObserver<HTMLDivElement>({
    threshold: 0.2,
    triggerOnce: true,
  });

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      ref={ref}
      role="button"
      tabIndex={0}
      aria-label={`View details for ${item.title}`}
      className={clsx(styles.card, isVisible && styles.visible)}
      style={{ transitionDelay: `${index * 0.1}s` }}
      onClick={onClick}
      onKeyDown={handleKeyDown}
    >
      <div className={styles.imageWrapper}>
        {item.imageUrl ? (
          <img
            src={item.imageUrl}
            alt={`${item.title} app screenshot`}
            className={styles.image}
            loading="lazy"
          />
        ) : (
          <div className={styles.imagePlaceholder}>
            <span className={styles.imagePlaceholderText}>{item.title}</span>
          </div>
        )}
        <div className={styles.overlay}>
          <FiExternalLink className={styles.icon} />
          <span className={styles.viewText}>View Details</span>
        </div>
      </div>

      <div className={styles.content}>
        <span className={clsx(
          styles.statusBadge,
          item.status === 'live' ? styles.statusLive : styles.statusDev
        )}>
          {item.status === 'live' ? 'Live on App Store' : 'In Development'}
        </span>

        <h3 className={styles.title}>{item.title}</h3>
        <p className={styles.description}>{item.description}</p>

        <div className={styles.technologies}>
          {item.technologies.slice(0, 3).map((tech) => (
            <span key={tech} className={styles.tech}>{tech}</span>
          ))}
          {item.technologies.length > 3 && (
            <span className={styles.tech}>+{item.technologies.length - 3} more</span>
          )}
        </div>
      </div>
    </div>
  );
};
